import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { InvoicesService } from './invoicesApi';
import { ApiResponse } from '../../../../interfaces/models/models/paginated-response.model';
import { IPaginationPayload } from '../../../../interfaces/shared/IPaginationPayload';

const initialState: any = {
    invoicesAreLoading: false,
    invoiceIsLoading: false,
    requestStatus: 'default',
    message: '',
};

export const tryToFetchInvoices = createAsyncThunk<any, IPaginationPayload>(
    'invoices/tryToFetchInvoices',
    async ({ pageNumber, pageSize, filters, data }) => {
        const result = await InvoicesService.tryToFetchInvoices(pageNumber, pageSize, data, filters);
        return result;
    },
);

export const tryToFetchSingleInvoice = createAsyncThunk<any, string>(
    'invoices/tryToFetchSingleInvoice',
    async (id: string) => {
        const result = await InvoicesService.tryToFetchSingleInvoice(id);
        return result?.data?.data;
    },
);

export const tryToAddInvoice = createAsyncThunk<ApiResponse<any>, any>(
    'invoices/tryToAddInvoice',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToAddInvoice(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToEditInvoice = createAsyncThunk<ApiResponse<any>, any>(
    'invoices/tryToEditInvoice',
    async ({ id, data }, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToEditInvoice(id, data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToDeleteInvoice = createAsyncThunk<ApiResponse<any>, string>(
    'invoices/tryToDeleteInvoice',
    async (id, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToDeleteInvoice(id);
            return result?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToMatchPayableInvoices = createAsyncThunk<ApiResponse<any>, any>(
    'invoices/tryToMatchPayableInvoices',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToMatchPayableInvoices(data);
            return result.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToMatchReceivableInvoices = createAsyncThunk<ApiResponse<any>, any>(
    'invoices/tryToMatchReceivableInvoices',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToMatchReceivableInvoices(data);
            return result.data?.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToCategorizePayableInvoices = createAsyncThunk<ApiResponse<any>, any>(
    'invoices/tryToCategorizePayableInvoices',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToCategorizePayableInvoices(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const tryToCategorizeReceivableInvoices = createAsyncThunk<ApiResponse<any>, any>(
    'invoices/tryToCategorizeReceivableInvoices',
    async (data: any, { rejectWithValue }) => {
        try {
            const result = await InvoicesService.tryToCategorizeReceivableInvoices(data);
            return result.data;
        } catch (error) {
            const err: any = error as AxiosError;
            return rejectWithValue(err?.response?.data?.message);
        }
    },
);

export const invoicesHotelSlice = createSlice({
    name: 'invoicesHotel',
    initialState,
    reducers: {},
    extraReducers: (builder) => {
        builder
            // tryToFetchInvoices
            .addCase(tryToFetchInvoices.pending, (state) => {
                state.invoicesAreLoading = true;
            })
            .addCase(tryToFetchInvoices.rejected, (state) => {
                state.invoicesAreLoading = false;
            })
            .addCase(tryToFetchInvoices.fulfilled, (state, action) => {
                state.invoicesAreLoading = false;
                state.invoices = action.payload;
            })

            // tryToFetchSingleInvoice
            .addCase(tryToFetchSingleInvoice.pending, (state) => {
                state.invoiceIsLoading = true;
            })
            .addCase(tryToFetchSingleInvoice.rejected, (state) => {
                state.invoiceIsLoading = false;
                state.invoiceDetails = undefined;
            })
            .addCase(tryToFetchSingleInvoice.fulfilled, (state, action) => {
                state.invoiceIsLoading = false;
                state.invoiceDetails = action.payload;
            })

            // tryToAddInvoice
            .addCase(tryToAddInvoice.pending, (state) => {
                state.invoiceIsLoading = true;
            })
            .addCase(tryToAddInvoice.rejected, (state) => {
                state.invoiceIsLoading = false;
            })
            .addCase(tryToAddInvoice.fulfilled, (state, action) => {
                state.invoiceIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg;
                if (state.invoices?.data) state.invoices.data.elements = [data, ...state.invoices?.data.elements || []];
            })

            // tryToDeleteInvoice
            .addCase(tryToDeleteInvoice.rejected, (state) => {
                state.invoiceIsLoading = false;
                state.requestStatus = 'error';
            })
            .addCase(tryToDeleteInvoice.fulfilled, (state, action) => {
                state.invoiceIsLoading = false;
                state.requestStatus = 'success';
                if (state.invoices?.data.elements)
                    state.invoices.data.elements =
                        { ...state }.invoices?.data.elements.filter((invoice: any) => invoice?._id !== action.meta.arg) || [];
            })

            // tryToEditInvoice
            .addCase(tryToEditInvoice.pending, (state) => {
                state.invoiceIsLoading = true;
            })
            .addCase(tryToEditInvoice.rejected, (state) => {
                state.invoiceIsLoading = false;
            })
            .addCase(tryToEditInvoice.fulfilled, (state, action) => {
                state.invoiceIsLoading = false;
                state.requestStatus = 'success';
                const data = action.payload.data || action.meta.arg?.data;
                if (state.invoices?.data.elements)
                    state.invoices.data.elements =
                        { ...state }.invoices?.data.elements.map((invoice: any) => {
                            if (invoice?._id === action.meta?.arg?.id) {
                                return {
                                    ...data || invoice || {},
                                }
                            } else {
                                return invoice
                            }
                        }) || [];
            })
    },
});

export default invoicesHotelSlice.reducer;